import { useEffect, useState } from 'react';

/**
 * Tailwind's `lg` breakpoint: where the sidebar is pinned beside the content. The iPad app reads it
 * here too (F-0519): its shell draws no navigation, so it never restates a width.
 */
export const SIDEBAR_PINNED_MIN_WIDTH_PX = 1024;
export const LG_MEDIA_QUERY = `(min-width: ${SIDEBAR_PINNED_MIN_WIDTH_PX}px)`;
/**
 * F-0235: the desktop layout — a list beside its detail panel, and no bottom bar — from 768 px, so
 * an iPad in portrait gets it. The Tailwind screen `desktop` is the same width; a guard keeps them
 * one value. Split views ask this, not `LG_MEDIA_QUERY`: below `lg` the sidebar slides over, so the
 * content has the whole width.
 */
export const DESKTOP_LAYOUT_MIN_WIDTH_PX = 768;
export const DESKTOP_LAYOUT_MEDIA_QUERY = `(min-width: ${DESKTOP_LAYOUT_MIN_WIDTH_PX}px)`;
/** Tailwind's `sm` and `md`, for rows that drop their least-scanned columns before they squeeze. */
export const SM_MEDIA_QUERY = '(min-width: 640px)';
export const MD_MEDIA_QUERY = '(min-width: 768px)';
/**
 * A device with no pointer finer than a finger, and nothing to hover: a phone or tablet touchscreen,
 * not a mouse/trackpad device (which stays `(pointer: coarse)` for emulation but keeps `hover`) and
 * not a hybrid laptop with a touchscreen *and* a trackpad (I-1044).
 */
export const TOUCH_ONLY_MEDIA_QUERY = '(pointer: coarse) and (hover: none)';

/**
 * Reactive media query. Reads synchronously on first render so the very first
 * paint already picks the right layout instead of flashing the wrong one.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const list = window.matchMedia(query);
    setMatches(list.matches);

    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * U-0024 (IP-27): a finger on the desktop layout — the iPad's sidebar, from 768 px. Under it the
 * phone layout lays its own menu out for a finger. Two queries, each the one its own rule names.
 */
export function useTouchDesktopLayout(): boolean {
  const touchOnly = useMediaQuery(TOUCH_ONLY_MEDIA_QUERY);
  const desktopLayout = useMediaQuery(DESKTOP_LAYOUT_MEDIA_QUERY);
  return touchOnly && desktopLayout;
}
